/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Select, Tooltip } from 'antd'
import { SwapOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type {
  OperationsNode,
  OperationsTeamNode,
} from '@sportninja/common/features/competition/operations/types/operations.types'
import { paragraphXSmall, weightMedium } from 'src/components/css'
import { useMoveTeam } from '../../hooks/useMoveTeam'
import {
  collectDivisionOptions,
  type DivisionOption,
} from '../../utils/collectDivisionOptions'

const translate = t as (key: string, options?: unknown) => string

/**
 * Two lines per option: the division, then the path leading to it. Defined at
 * module scope — inline it would be a new component type on every render.
 */
const renderOption = (option: { data: Record<string, unknown> }) => {
  const division = option.data.division as DivisionOption
  return (
    <span css={optionStyle}>
      <span className='division-header'>
        <span className='division-name'>{division.name}</span>
        {/* How full the destination already is — a reorder lands at the end of
            this list, so its current size is the useful context. */}
        <span className='division-count'>
          {division.teamCount}{' '}
          {/* i18next v25 resolves plurals via `_one`/`_other` suffixes, but the
              lang files only carry the legacy `_plural` — so passing `count`
              silently falls back to the singular. Pick the key explicitly. */}
          {translate(
            division.teamCount === 1 ? 'common:team' : 'common:team_plural'
          )}
        </span>
      </span>
      {/* Where the division lives — two same-named divisions in different
          conferences are otherwise indistinguishable. */}
      {division.path && <span className='division-path'>{division.path}</span>}
    </span>
  )
}

interface MoveTeamSelectProps {
  team: OperationsTeamNode
  /** The FULL tree, so every division is offered — not the search-filtered one. */
  tree: OperationsNode[]
  rootScheduleId: string
  /** Follows the moved team so the panel keeps showing it in its new division. */
  onMoved?: (teamNodeId: string) => void
  /**
   * Competition-level registration flag. While on, teams are placed through
   * the Registration tab, so the control is shown disabled with a tooltip
   * pointing there rather than hidden.
   */
  isRegistrationOn?: boolean
}

/**
 * Inline "move to division" control for the panel header — the same move the
 * row action's modal performs, without the modal.
 *
 * `placement` is deliberately omitted: `useMoveTeam` then sends
 * `seed_action: 'after'` with no anchor team, which the server treats as an
 * append. That is exactly "last item of the destination division", so no
 * ordering has to be computed here.
 *
 * Options are DIVISIONS only (`collectDivisionOptions`), not the wider "can
 * hold teams" set the modal uses — that one also returns childless
 * conferences, which are not valid parents for a team.
 */
const MoveTeamSelect = ({
  team,
  tree,
  rootScheduleId,
  onMoved,
  isRegistrationOn = false,
}: MoveTeamSelectProps) => {
  'use memo'

  const { moveTeam, isMoving } = useMoveTeam(rootScheduleId)

  // The team's current division is not a destination — moving it there is a
  // no-op the API would still process.
  const options = collectDivisionOptions(tree)
    .filter((division) => division.scheduleId !== team.scheduleId)
    .map((division) => ({
      value: division.scheduleId,
      // Searched against by `optionFilterProp` — the path is included so
      // typing a conference name narrows to its divisions.
      label: division.path
        ? `${division.name} ${division.path}`
        : division.name,
      division,
    }))

  const handleChange = async (toScheduleId: string) => {
    // `useMoveTeam` awaits the tree refetch, so by the time this resolves the
    // moved team exists at its new id. Selecting it any earlier unmounts the
    // panel — `useTreeSelection` cannot find an id the tree does not hold yet.
    await moveTeam({
      teamId: team.teamId,
      fromScheduleId: team.scheduleId,
      toScheduleId,
    })
    // Team node ids are `${scheduleId}-${teamId}`, so the moved team's id
    // changes with its parent — the caller re-selects it under the new one.
    onMoved?.(`${toScheduleId}-${team.teamId}`)
  }

  if (options.length === 0) {
    return null
  }

  const select = (
    <Select
      css={selectStyle}
      popupClassName='move-team-dropdown'
      // Controlled as always-empty: this is an action, not a stored value. The
      // team's location is shown by the breadcrumb, not by this control.
      value={null}
      placeholder={capitalizeWords(translate('Web:moveTeam'))}
      options={options}
      onChange={handleChange}
      loading={isMoving}
      disabled={isMoving || isRegistrationOn}
      showSearch
      optionFilterProp='label'
      popupMatchSelectWidth={false}
      suffixIcon={<SwapOutlined />}
      aria-label={translate('Web:moveTeam')}
      optionRender={renderOption}
    />
  )

  if (!isRegistrationOn) {
    return select
  }

  // A disabled Select swallows pointer events, so the tooltip hangs off a
  // wrapper that still receives the hover.
  return (
    <Tooltip title={translate('Web:moveTeamRegistrationOn')} placement='top'>
      <span css={disabledWrapperStyle}>{select}</span>
    </Tooltip>
  )
}

/**
 * Carries the outlined `EntityActionButton` treatment — PRIMARY border, 8px
 * radius, transparent fill, 34px tall — so the select reads as one of the
 * buttons it sits beside rather than as a form field.
 */
const selectStyle = css`
  min-width: 150px;
  /* The ROOT must carry the height too, not just the selector: the arrow is
     absolutely positioned against this element, and antd centres it with
     top: 50%. Sizing only the selector left the root at its default ~40px, so
     that 50% resolved to 20px instead of 17px and the icon sat ~3px low. */
  height: 34px;

  .ant-select-selector {
    ${paragraphXSmall}
    ${weightMedium}
    height: 34px !important;
    padding: 0 11px !important;
    border-radius: 8px !important;
    background-color: transparent !important;
    border: 1px solid ${colors.PRIMARY} !important;
    color: ${colors.NEUTRAL_0};
    align-items: center;
    transition: opacity 0.15s ease;
  }

  &:not(.ant-select-disabled):hover .ant-select-selector {
    opacity: 0.85;
    border-color: ${colors.PRIMARY} !important;
  }

  /* antd's focus/active treatment is a blue glow — replaced so the control
     stays on the tenant palette. */
  &.ant-select-focused .ant-select-selector {
    box-shadow: none !important;
    border-color: ${colors.PRIMARY} !important;
  }

  &.ant-select-disabled .ant-select-selector {
    opacity: 0.5;
  }

  /* 32px = the 34px control minus its 1px borders, so the text sits centred. */
  .ant-select-selection-placeholder,
  .ant-select-selection-item,
  .ant-select-selection-search-input {
    color: ${colors.NEUTRAL_0};
    height: 32px !important;
    line-height: 32px !important;
  }

  /* Presentation only — antd's own top: 50% / margin-top: -6px centring is
     correct once the ROOT is 34px (see above). */
  .ant-select-arrow {
    color: ${colors.NEUTRAL_0};
    /* 12px, matching the height antd's margin-top: -6px assumes — a larger
       glyph reintroduces a half-pixel offset. */
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const disabledWrapperStyle = css`
  display: inline-flex;
  cursor: not-allowed;
`

/* Two lines per option: the division (with its team count), then the path
   that leads to it. */
const optionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 2px 0;

  .division-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  .division-name {
    ${paragraphXSmall}
    color: ${colors.NEUTRAL_0};
  }

  .division-count {
    font-size: 11px;
    line-height: 1.3;
    color: ${colors.NEUTRAL_100};
    white-space: nowrap;
  }

  .division-path {
    font-size: 11px;
    line-height: 1.3;
    color: ${colors.NEUTRAL_100};
  }
`

export default MoveTeamSelect
